import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { getSafeTimestamp, findAtPlaybackTime } from './visualizeData.constants';
import SignalSearch from './SignalSearch';

/**
 * Accessor used with findClosestByTime for chartData arrays.
 * chartData points store their timestamp as .timestamp (string, Date, or number).
 */
const getChartTs = (point) => getSafeTimestamp(point.timestamp);

/**
 * TablePanel — real-time signal values table synced to playback position.
 *
 * Reads values directly from each signal's already-extracted chartData
 * using binary search at the current playback time — no separate timeline API.
 *
 * Props:
 *   currentTimeSec    — elapsed playback time in seconds (from usePlayback)
 *   visualizedSignals — array of signal objects from useBuslogSignals
 *   globalStartTs     — authoritative file start timestamp (ns) from backend
 *   playbackTimestamp — pre-formatted absolute timestamp string (from presenter)
 */
const TablePanel = ({
  currentTimeSec = 0,
  visualizedSignals = [],
  globalStartTs,
  playbackTimestamp = '',
  onRemoveSignal,
  prep,
  signals,
  filePath,
  isSaved,
  handleSaveSearchClick,
  currentSignalsParam
}) => {

  const renderSearch = () => {
    if (!prep?.isFileReady) return null;
    return (
      <Box sx={{ width: '100%', borderBottom: '1px solid', borderBottomColor: 'divider', display: 'flex', alignItems: 'center', pr: 1 }}>
        <Box sx={{ flex: 1 }}>
          <SignalSearch
            filePath={filePath}
            isFileReady={prep.isFileReady}
            signalCount={prep.signalCount}
            signals={signals}
          />
        </Box>
        <Tooltip title={isSaved ? 'Unsave Signals' : 'Save Signals'} arrow>
          <span>
            <IconButton
              onClick={handleSaveSearchClick}
              disabled={!currentSignalsParam}
              size="small"
              sx={{ color: isSaved ? 'success.main' : 'primary.main', ml: 1 }}
              aria-label={isSaved ? 'Unsave Signals' : 'Save Signals'}
            >
              {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  // Build table rows by looking up each signal's value at the target time
  const rows = useMemo(() => {
    return visualizedSignals.map((sig) => {
      const data = sig.chartData;
      const point = findAtPlaybackTime(data, currentTimeSec, getChartTs, globalStartTs);

      let valueStr = '—';
      let valueNum = '—';

      if (point) {
        valueNum = point.value_num != null ? point.value_num : '—';
        valueStr = point.value_str != null ? String(point.value_str) : '—';
      }

      return {
        signal: sig.signalName,
        originalName: sig.originalSignalName,
        value_str: valueStr,
        value_num: valueNum,
      };
    });
  }, [visualizedSignals, currentTimeSec, globalStartTs]);

  // ---- No signals selected ----
  if (visualizedSignals.length === 0) {
    return (
      <Box className="table-panel-container table-panel-empty" sx={{ justifyContent: 'flex-start' }}>
        {renderSearch()}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
          <ArrowUpwardIcon className="search-pulse-arrow" sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" color="text.primary" gutterBottom>
            No signals selected
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 300 }}>
            Use the search bar above to find and add signals to your visualization.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="table-panel-container">
      {renderSearch()}
      {/* Table */}
      <TableContainer className="table-panel-body">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className="table-panel-th">Signal</TableCell>
              <TableCell className="table-panel-th" align="right">Value (String)</TableCell>
              <TableCell className="table-panel-th" align="right">Value (Numeric)</TableCell>
              <TableCell className="table-panel-th table-panel-action-cell" align="center"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.signal} hover className="table-panel-row">
                <TableCell className="table-panel-signal-cell">
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                    {row.signal}
                  </Typography>
                </TableCell>
                <TableCell align="right" className="table-panel-value-cell">
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                    {row.value_str}
                  </Typography>
                </TableCell>
                <TableCell align="right" className="table-panel-value-cell">
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                    {typeof row.value_num === 'number' ? row.value_num.toLocaleString(undefined, { maximumFractionDigits: 6 }) : row.value_num}
                  </Typography>
                </TableCell>
                <TableCell align="center" className="table-panel-value-cell">
                  {onRemoveSignal && (
                    <Tooltip title="Remove signal">
                      <IconButton size="small" onClick={() => onRemoveSignal(row.signal)} className="table-panel-remove-button">
                        <CloseIcon className="table-panel-remove-icon" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TablePanel;
